import {
  IOrganisation,
  IOrganisationSubscription,
  IPractice,
  IStripeInvoice,
  OrganisationCollection,
  OrganisationSubscriptionCollection,
} from '@principle-theorem/principle-core/interfaces';
import {
  all$,
  asReffable,
  asyncForEach,
  AtLeast,
  CollectionReference,
  collectionGroupQuery,
  DocumentReference,
  Firestore,
  IReffable,
  isSameRef,
  multiFilter,
  resolveDocRefs,
  subCollection,
  undeletedFilter,
  where,
  WithRef,
} from '@principle-theorem/shared';
import { compact, differenceWith, first } from 'lodash';
import { Observable } from 'rxjs';
import { Brand } from '../brand';
import { Organisation } from './organisation';

export class OrganisationSubscription {
  static init(
    overrides: AtLeast<
      IOrganisationSubscription,
      'subscriptionId' | 'customerId'
    >
  ): IOrganisationSubscription {
    return {
      deleted: false,
      billingMeters: [],
      practiceRefs: [],
      ...overrides,
    };
  }

  static stripeInvoiceCol(
    subsciption: IReffable<IOrganisationSubscription>
  ): CollectionReference<IStripeInvoice> {
    return subCollection<IStripeInvoice>(
      subsciption.ref,
      OrganisationSubscriptionCollection.StripeInvoices
    );
  }

  static stripeInvoices$(
    subsciption: IReffable<IOrganisationSubscription>
  ): Observable<WithRef<IStripeInvoice>[]> {
    return all$(OrganisationSubscription.stripeInvoiceCol(subsciption)).pipe(
      multiFilter((invoice) => !invoice.deleted)
    );
  }

  static organisationRef(
    subsciption: IReffable<IOrganisationSubscription>
  ): DocumentReference<IOrganisation> {
    return Firestore.getParentDocRef<IOrganisation>(subsciption.ref);
  }

  static includesPracticeRef(
    subscription: WithRef<IOrganisationSubscription>,
    practiceRef: DocumentReference<IPractice>
  ): boolean {
    return subscription.practiceRefs.some((subPracticeRef) =>
      isSameRef(subPracticeRef, practiceRef)
    );
  }

  static isPracticeSpecific(
    subscription: WithRef<IOrganisationSubscription>
  ): boolean {
    return subscription.practiceRefs.length > 0;
  }

  /**
   * Canonical rule for picking the subscription covering a practice: its own
   * practice-specific subscription if one exists, else the org-wide one (no
   * `practiceRefs`). Shared by the server resolver, the Angular gate and
   * {@link Organisation.getSubscription} so they cannot drift — a mismatch
   * here silently grants or withholds paid features.
   */
  static selectForPractice<T extends IOrganisationSubscription>(
    subscriptions: T[],
    practiceRef?: DocumentReference<IPractice>
  ): T | undefined {
    const activeSubscriptions = subscriptions.filter(
      (subscription) => !subscription.deleted
    );

    const practiceMatch = practiceRef
      ? activeSubscriptions.find((subscription) =>
          subscription.practiceRefs.some((ref) => isSameRef(ref, practiceRef))
        )
      : undefined;

    return (
      practiceMatch ??
      activeSubscriptions.find(
        (subscription) => subscription.practiceRefs.length === 0
      )
    );
  }

  /**
   * Exact reverse-lookup from a Stripe `subscriptionId` to its own document,
   * used instead of a `customerId` lookup because one customer can own several
   * subscriptions (practice-specific ones via `practiceRefs`), so routing by
   * `customerId` can apply a delete for an old subscription to the customer's
   * current one.
   *
   * Returns `undefined` instead of throwing when nothing matches — retrying
   * cannot conjure a missing document, so callers warn and complete.
   */
  static async findBySubscriptionId(
    subscriptionId: string
  ): Promise<WithRef<IOrganisationSubscription> | undefined> {
    const matches = (
      await Firestore.getDocs(
        collectionGroupQuery<IOrganisationSubscription>(
          OrganisationCollection.Subscriptions
        ),
        where('subscriptionId', '==', subscriptionId)
      )
    ).filter(undeletedFilter);

    return first(matches);
  }

  static async getSubscriptionPractices(
    subscription: WithRef<IOrganisationSubscription>
  ): Promise<WithRef<IPractice>[]> {
    if (OrganisationSubscription.isPracticeSpecific(subscription)) {
      return subscription.practiceRefs
        ? resolveDocRefs(subscription.practiceRefs)
        : [];
    }
    const org = asReffable(
      OrganisationSubscription.organisationRef(subscription)
    );
    const subscriptions = (
      await Firestore.getDocs(Organisation.subscriptionCol(org))
    ).filter((sub) => !sub.deleted);
    const practicesWithSub = compact(
      subscriptions.flatMap((orgSub) => orgSub.practiceRefs)
    );

    const brands = (await Firestore.getDocs(Organisation.brandCol(org))).filter(
      (brand) => !brand.deleted
    );
    const practices = await asyncForEach(brands, (brand) =>
      Firestore.getDocs(Brand.practiceCol(brand))
    );
    return differenceWith(practices.flat(), practicesWithSub, isSameRef);
  }
}
